import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button } from 'react-bootstrap';
import { FaIdBadge, FaMapMarkerAlt, FaSave, FaTimes, FaUserEdit } from 'react-icons/fa';
import Swal from 'sweetalert2';
import AdminFormLayout from './AdminFormLayout';
import Navigation from './Navigation';
import { readStoredAdminUser } from './adminAccessControl';
import DatePicker from 'react-datepicker';
import { fetchActiveOutletModules } from './outletModuleAccess';
import {
    EMPLOYEE_ROLE_CODES,
    getEmployeeRoleOptions,
    outletSupportsKitchenRole,
} from './employeeRoleOptions';
import "react-datepicker/dist/react-datepicker.css";

function EditKaryawanForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const currentUser = readStoredAdminUser();
    const isOutletActor = currentUser.actor_type === 'outlet';
    const [outlets, setOutlets] = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [moduleLoadError, setModuleLoadError] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        outlet: currentUser.actor_type === 'outlet' ? String(currentUser.outlet_id || '') : '',
        username: '',
        password: '',
        role: 1,
        jabatan: '',
        nama: '',
        alamat: '',
        tgl_lahir: '',
        no_telp: '',
        modules: [],
    });

    const selectedOutlet = outlets.find(outlet => String(outlet.id) === String(formData.outlet || ''));
    const roleOptions = getEmployeeRoleOptions(selectedOutlet?.outlet_type);

    useEffect(() => {
        fetchOutlets();
        if (id) {
            fetchKaryawanDetails();
            fetchKaryawanModuleAssignment();
        }
    }, [id]);

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        let active = true;

        const syncAvailableModules = async () => {
            if (!token || !formData.outlet) {
                if (active) {
                    setModuleLoadError('');
                    setModuleOptions([]);
                    setFormData(prev => ({
                        ...prev,
                        modules: [],
                    }));
                }
                return;
            }

            try {
                if (active) {
                    setModuleLoadError('');
                }
                const allowedModules = await fetchActiveOutletModules(token, formData.outlet);
                if (!active) {
                    return;
                }

                const allowedIdSet = new Set(allowedModules.map(module => String(module.ID)));
                setModuleOptions(allowedModules);
                setFormData(prev => ({
                    ...prev,
                    modules: prev.modules.filter(moduleId => allowedIdSet.has(String(moduleId))),
                }));
            } catch (err) {
                console.error(err);
                if (active) {
                    setModuleLoadError(err.message || 'Failed to load active outlet modules.');
                    setModuleOptions([]);
                }
            }
        };

        syncAvailableModules();

        return () => {
            active = false;
        };
    }, [formData.outlet]);

    useEffect(() => {
        if (!formData.role) {
            return;
        }
        if (formData.outlet && !selectedOutlet) {
            return;
        }

        const selectedRole = Number(formData.role);
        const roleStillAllowed = roleOptions.some(option => option.value === selectedRole);
        if (!roleStillAllowed) {
            setFormData(prev => ({
                ...prev,
                role: EMPLOYEE_ROLE_CODES.STAFF,
            }));
        }
    }, [formData.role, roleOptions]);

    const fetchOutlets = async () => {
        const token = localStorage.getItem('sessionToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ limit: 100, start: 0 }),
            });
            const data = await response.json();
            if (String(data.error) === "0") {
                const outletResults = Array.isArray(data.results) ? data.results : [];
                const visibleOutlets = isOutletActor
                    ? outletResults.filter(outlet => String(outlet.id) === String(currentUser.outlet_id || currentUser.outletId || ''))
                    : outletResults;
                setOutlets(visibleOutlets);
                setFormData(prev => {
                    if (prev.outlet) {
                        return prev;
                    }

                    const preferredOutlet =
                        visibleOutlets.find(outlet => String(outlet.id) === String(currentUser.outlet_id || currentUser.outletId || '')) ||
                        (isOutletActor && visibleOutlets.length === 1 ? visibleOutlets[0] : null);

                    if (!preferredOutlet) {
                        return prev;
                    }

                    return {
                        ...prev,
                        outlet: String(preferredOutlet.id),
                    };
                });
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Failed to fetch outlets', 'error');
        }
    };

    const fetchKaryawanDetails = async () => {
        const token = localStorage.getItem('sessionToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}getkaryawanid`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ id: parseInt(id) }),
            });
            const responseData = await response.json();
            console.log(responseData);
            if (String(responseData.error) === '0' && responseData.data) {
                const employeeData = responseData.data;
    
                setFormData(prev => ({
                    ...prev,
                    id: employeeData.id,
                    outlet: String(employeeData.outlet),
                    username: employeeData.username,
                    password: '',
                    role: employeeData.role,
                    jabatan: employeeData.jabatan,
                    nama: employeeData.nama,
                    alamat: employeeData.alamat,
                    tgl_lahir: employeeData.tgl_lahir ? String(employeeData.tgl_lahir).split('T')[0] : '',
                    no_telp: employeeData.no_telp,
                }));
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Failed to fetch employee details', 'error');
        }
    };

    const fetchKaryawanModuleAssignment = async () => {
        const token = localStorage.getItem('sessionToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}getkaryawanmodules`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ id_karyawan: parseInt(id, 10) }),
            });

            const responseData = await response.json();
            if (String(responseData.error) === '0' && responseData.data) {
                setFormData(prev => ({
                    ...prev,
                    modules: Array.isArray(responseData.data.modules)
                        ? responseData.data.modules.map(moduleId => String(moduleId))
                        : [],
                }));
            }
        } catch (err) {
            console.error('Failed to fetch employee module assignment:', err);
        }
    };

    const handleModuleToggle = (moduleId) => {
        setFormData(prev => {
            const currentModules = new Set(prev.modules);
            if (currentModules.has(moduleId)) {
                currentModules.delete(moduleId);
            } else {
                currentModules.add(moduleId);
            }

            return {
                ...prev,
                modules: Array.from(currentModules),
            };
        });
    };
        

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('sessionToken');
        
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}editkaryawan`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    id: parseInt(formData.id),
                    outlet: parseInt(formData.outlet),
                    username: formData.username,
                    password: formData.password || undefined, // Only send if password is changed
                    role: parseInt(formData.role),
                    jabatan: formData.jabatan,
                    nama: formData.nama,
                    alamat: formData.alamat,
                    tgl_lahir: formData.tgl_lahir,
                    no_telp: formData.no_telp,
                    modules: formData.modules.map(Number),
                })
            });

            const data = await response.json();
            if (data.error === "0") {
                Swal.fire('Success', 'Employee updated successfully', 'success');
                navigate('/employees');
            } else {
                Swal.fire('Error', data.message || 'Failed to update employee', 'error');
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Failed to update employee', 'error');
        }
    };

    const handleDateChange = (date) => {
        const formattedDate = date ? date.toISOString().split('T')[0] : '';
        setFormData({ ...formData, tgl_lahir: formattedDate });
    };

    return (
        <div>
            <Navigation />
            <AdminFormLayout
                icon={FaUserEdit}
                eyebrow="Team maintenance"
                title="Edit Employee"
                description="Perbarui outlet, role, dan data profil karyawan tanpa mengubah alur kerja admin."
                breadcrumbs={[
                    { label: 'Dashboard', to: '/dashboardadmin' },
                    { label: 'Employees', to: '/employees' },
                    { label: 'Edit Employee' },
                ]}
                tips={[
                    'Kosongkan password bila tidak ingin mengganti kredensial login saat ini.',
                    'Perubahan role akan memengaruhi akses modul yang bisa dipakai karyawan.',
                    'Pastikan nomor telepon dan alamat tetap valid untuk kebutuhan operasional.',
                ]}
            >
                <Form onSubmit={handleSubmit}>
                    <section className="form-section">
                        <div className="form-section__header">
                            <span className="form-section__icon">
                                <FaIdBadge />
                            </span>
                            <div className="form-section__copy">
                                <h2 className="form-section__title">Access setup</h2>
                                <p className="form-section__description">
                                    Ubah outlet, username, password, dan role karyawan dari satu panel yang konsisten.
                                </p>
                            </div>
                        </div>

                        <div className="form-grid">
                            <Form.Group className="field-stack span-6">
                                <Form.Label>Outlet</Form.Label>
                                <Form.Select
                                    value={formData.outlet}
                                    onChange={(e) => setFormData({ ...formData, outlet: e.target.value })}
                                    disabled={isOutletActor}
                                    required
                                >
                                    <option value="">Select Outlet</option>
                                    {outlets.map(outlet => (
                                        <option key={outlet.id} value={outlet.id}>
                                            {outlet.outlet_name}
                                        </option>
                                    ))}
                                </Form.Select>
                                {isOutletActor ? (
                                    <Form.Text>
                                        Akun outlet hanya dapat mengelola employee pada outlet ini.
                                    </Form.Text>
                                ) : null}
                            </Form.Group>

                            <Form.Group className="field-stack span-6">
                                <Form.Label>Role</Form.Label>
                                <Form.Select
                                    value={formData.role}
                                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                                >
                                    {roleOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text>
                                    {outletSupportsKitchenRole(selectedOutlet?.outlet_type)
                                        ? 'Role Kitchen tersedia untuk outlet dengan workflow restoran.'
                                        : 'Role Kitchen hanya muncul untuk outlet dengan workflow restaurant.'}
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="field-stack span-6">
                                <Form.Label>Username</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.username}
                                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="field-stack span-6">
                                <Form.Label>Password (Leave blank to keep current password)</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                                />
                            </Form.Group>
                        </div>
                    </section>

                    <section className="form-section">
                        <div className="form-section__header">
                            <span className="form-section__icon">
                                <FaMapMarkerAlt />
                            </span>
                            <div className="form-section__copy">
                                <h2 className="form-section__title">Employee profile</h2>
                                <p className="form-section__description">
                                    Lengkapi perubahan identitas karyawan agar data di sistem tetap akurat.
                                </p>
                            </div>
                        </div>

                        <div className="form-grid">
                            <Form.Group className="field-stack span-6">
                                <Form.Label>Position</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.jabatan}
                                    onChange={(e) => setFormData({...formData, jabatan: e.target.value})}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="field-stack span-6">
                                <Form.Label>Full Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.nama}
                                    onChange={(e) => setFormData({...formData, nama: e.target.value})}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="field-stack span-6">
                                <Form.Label>Birth Date</Form.Label>
                                <DatePicker
                                    selected={formData.tgl_lahir ? new Date(formData.tgl_lahir) : null}
                                    onChange={handleDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    className="form-control"
                                    placeholderText="Select date"
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="field-stack span-6">
                                <Form.Label>Phone Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.no_telp}
                                    onChange={(e) => setFormData({...formData, no_telp: e.target.value})}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="field-stack span-12">
                                <Form.Label>Address</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={formData.alamat}
                                    onChange={(e) => setFormData({...formData, alamat: e.target.value})}
                                    required
                                />
                            </Form.Group>
                        </div>
                    </section>

                    <section className="form-section">
                        <div className="form-section__header">
                            <span className="form-section__icon">
                                <FaUserEdit />
                            </span>
                            <div className="form-section__copy">
                                <h2 className="form-section__title">Access keys</h2>
                                <p className="form-section__description">
                                    Hanya modul yang aktif pada outlet karyawan ini yang bisa dipertahankan atau ditambahkan.
                                </p>
                            </div>
                        </div>

                        <div className="field-stack">
                            <Form.Label>Assigned modules</Form.Label>
                            <div className="check-grid">
                                {moduleLoadError ? (
                                    <p className="text-danger mb-0">{moduleLoadError}</p>
                                ) : moduleOptions.length > 0 ? (
                                    moduleOptions.map(module => (
                                        <Form.Check
                                            key={module.ID}
                                            type="checkbox"
                                            label={`${module.Modulename}${module.Modulelink ? ` • ${module.Modulelink}` : ''}`}
                                            checked={formData.modules.includes(String(module.ID))}
                                            onChange={() => handleModuleToggle(String(module.ID))}
                                        />
                                    ))
                                ) : !formData.outlet ? (
                                    <p className="text-muted mb-0">Select an outlet first to load the active module list.</p>
                                ) : (
                                    <p className="text-muted mb-0">No active modules are configured for this outlet.</p>
                                )}
                            </div>
                            <Form.Text>
                                Modul di luar konfigurasi outlet tidak akan tampil dan tidak bisa disimpan ke akun karyawan.
                            </Form.Text>
                        </div>
                    </section>

                    <div className="form-actions">
                        <Button variant="secondary" onClick={() => navigate('/employees')}>
                            <FaTimes /> Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                            <FaSave /> Update Employee
                        </Button>
                    </div>
                </Form>
            </AdminFormLayout>
        </div>
    );
}

export default EditKaryawanForm;
